import { useState } from "react";
import { SendHorizonal, X } from "lucide-react";
import { createBhpRequest } from "./data.js";
import { friendlyError } from "./lib/format.js";

function defaultQty(item) {
  const factor = Number(item.conversion_factor) > 0 ? Number(item.conversion_factor) : 1;
  const gap = Number(item.target_stock) > Number(item.current_stock)
    ? Number(item.target_stock) - Number(item.current_stock)
    : factor;
  return Math.max(1, Math.ceil(gap / factor));
}

function purchaseUnit(item) {
  return item.purchase_unit || item.unit || "unit";
}

const ACTIVE_STOCK_DESTINATIONS = [
  { value: "WELLNESS", label: "Wellness / TCM" },
  { value: "ESTETIK", label: "Estetik" },
  { value: "DENTAL", label: "Dental" },
  { value: "DOKTER_UMUM", label: "Dokter Umum" },
  { value: "APOTIK", label: "Apotik" },
];

export function BHPRequestModal({ item, items, from_division, onClose, onSaved }) {
  const requestItems = items?.length ? items : item ? [item] : [];
  const isBatch = requestItems.length > 1;
  const defaultDestination = from_division
    || requestItems.find((row) => row.division && row.division !== "ALL")?.division
    || "WELLNESS";
  const [qty, setQty] = useState(item ? defaultQty(item) : 1);
  const [batchQty, setBatchQty] = useState(() =>
    Object.fromEntries(requestItems.map((row) => [row.id, defaultQty(row)]))
  );
  const [destinationDivision, setDestinationDivision] = useState(defaultDestination);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    if (!requestItems.length) {
      setError("Tidak ada item yang dipilih.");
      return;
    }
    if (!isBatch && Number(qty) < 1) {
      setError("Jumlah harus minimal 1.");
      return;
    }
    if (isBatch && requestItems.some((row) => Number(batchQty[row.id]) < 1)) {
      setError("Semua jumlah item harus minimal 1.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      await Promise.all(requestItems.map((row) =>
        createBhpRequest({
          bhp_item_id: row.id,
          item_name: row.name,
          unit: purchaseUnit(row),
          requested_qty: Number(isBatch ? batchQty[row.id] : qty),
          notes: notes.trim() || null,
          from_division: from_division || (row.division && row.division !== "ALL" ? row.division : destinationDivision),
        })
      ));
      onSaved();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal-card" role="dialog" aria-modal="true" style={{ maxWidth: isBatch ? 620 : 420 }}>
        <header className="modal-head">
          <div className="grow">
            <h2>{isBatch ? "Permintaan Stok Massal" : "Permintaan Stok ke Gudang"}</h2>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              {isBatch ? `${requestItems.length} item dipilih` : (
                <>
                  {item.name} · Stok saat ini:{" "}
                  <strong style={{ color: "#c53030" }}>
                    {Number(item.current_stock)} {item.base_unit || item.unit || ""}
                  </strong>
                </>
              )}
              {from_division ? (
                <span style={{ marginLeft: 8, background: "#eff6ff", color: "#2563eb", borderRadius: 4, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                  dari {from_division}
                </span>
              ) : null}
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        <form className="modal-body" onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {error ? <div className="alert alert-danger">{error}</div> : null}

          {isBatch ? (
            <div style={{ display: "grid", gap: 8, maxHeight: 360, overflow: "auto", paddingRight: 4 }}>
              {requestItems.map((row, index) => (
                <div
                  key={row.id}
                  style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 10, alignItems: "center", padding: "8px 10px", border: "1px solid #e8e2d5", borderRadius: 8, background: "#fff" }}
                >
                  <div>
                    <strong style={{ display: "block", fontSize: 14 }}>{row.name}</strong>
                    <small style={{ color: "#6b7280" }}>
                      Stok {Number(row.current_stock)} {row.unit || ""} · Target {Number(row.target_stock)} {row.unit || ""}
                    </small>
                  </div>
                  <input
                    aria-label={`Jumlah ${row.name}`}
                    type="number"
                    min="1"
                    step="1"
                    value={batchQty[row.id] || 1}
                    onChange={(event) => setBatchQty((current) => ({ ...current, [row.id]: event.target.value }))}
                    style={{ padding: "8px 10px", border: "1px solid #e8e2d5", borderRadius: 8, fontSize: 14 }}
                    autoFocus={index === 0}
                  />
                </div>
              ))}
            </div>
          ) : (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b6555" }}>
                Jumlah kemasan yang diminta ({purchaseUnit(item)}) *
              </span>
              <input
                type="number"
                min="1"
                step="1"
                value={qty}
                onChange={(event) => setQty(event.target.value)}
                style={{ padding: "9px 12px", border: "1px solid #e8e2d5", borderRadius: 8, fontSize: 15 }}
                autoFocus
              />
            </label>
          )}

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#6b6555" }}>Catatan (opsional)</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Keterangan urgensi, spesifikasi, dll."
              style={{ padding: "9px 12px", border: "1px solid #e8e2d5", borderRadius: 8, fontSize: 14, resize: "vertical" }}
            />
          </label>

          {!isBatch && item.target_stock > 0 ? (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#15803d" }}>
              Target stok: <strong>{Number(item.target_stock)} {item.base_unit || item.unit || ""}</strong>.
              {" "}Konversi: <strong>1 {purchaseUnit(item)} = {Number(item.conversion_factor) || 1} {item.base_unit || item.unit || ""}</strong>.
            </div>
          ) : null}

          {!from_division ? (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b6555" }}>Tujuan stok diterima</span>
              <select
                value={destinationDivision}
                onChange={(event) => setDestinationDivision(event.target.value)}
                style={{ padding: "9px 12px", border: "1px solid #e8e2d5", borderRadius: 8, fontSize: 14, background: "#fff" }}
              >
                {ACTIVE_STOCK_DESTINATIONS.map((destination) => (
                  <option key={destination.value} value={destination.value}>{destination.label}</option>
                ))}
              </select>
              <small style={{ color: "#6b7280" }}>
                Dipakai bila item berlaku untuk semua divisi atau permintaan dibuat dari tab Semua.
              </small>
            </label>
          ) : null}
        </form>

        <footer className="modal-actions">
          <button className="button button-quiet" onClick={onClose} disabled={busy}>Batal</button>
          <button className="button button-primary" onClick={submit} disabled={busy}>
            <SendHorizonal size={16} />
            {busy ? "Mengirim..." : isBatch ? `Kirim ${requestItems.length} Permintaan` : "Kirim Permintaan"}
          </button>
        </footer>
      </section>
    </div>
  );
}
